/**
 * "Have your say" — the pure rules of the end-of-article poll sets (SoundGuys, 2026-10-27).
 * Framework-free: the jest suite drives them in node. The React side is `HaveYourSay.tsx`.
 *
 * Plan: apps/sound-guys/docs/have-your-say-plan.md; dossier
 * https://claude.ai/artifact/NDWFGgHZBNq6huQVrm9e1j.
 */
import type { DSPoll, DSPollSet, DSPollSetKind } from 'shared-definitions/types'

/**
 * At most this many chips; the rest are dropped, not scrolled. Room for the reviews tree
 * (Guillaume, 2026-10-28): this article, the article's type, its scope's other types (headphones
 * has three), the whole scope — and the product set of phase 2.
 */
export const MAX_SETS = 6

/** The small-caps kind label of a card's tag; the tree sets carry their name alone. */
export const SET_KIND_LABEL: Record<DSPollSetKind, string> = {
  article: 'This article',
  product: 'Product',
  topic: 'Topic',
  scope: 'All',
}

const KIND_RANK: Record<DSPollSetKind, number> = { article: 0, product: 1, topic: 2, scope: 3 }

/**
 * The chip's name: "This article", the product's or type's name, "All earbuds" for the scope —
 * the pills read as the walk up the reviews tree (Wireless earbuds → Wired earbuds → All earbuds).
 */
export function setChipName(set: Pick<DSPollSet, 'kind' | 'label'>): string {
  switch (set.kind) {
    case 'article':
      return SET_KIND_LABEL.article
    case 'scope':
      return `All ${set.label.toLowerCase()}`
    default:
      return set.label
  }
}

/** A card's tag: "This article", "Product · X", "Topic · Wireless earbuds", "All earbuds". */
export function setTag(set: Pick<DSPollSet, 'kind' | 'label'>): string {
  switch (set.kind) {
    case 'article':
    case 'scope':
      return setChipName(set)
    default:
      return `${SET_KIND_LABEL[set.kind]} · ${set.label}`
  }
}

/** A set's key among the chips: a kind can come several times (one topic set per type), an id once per kind. */
export function setKey(set: Pick<DSPollSet, 'kind' | 'id'>): string {
  return `${set.kind}:${set.id}`
}

/**
 * The sets in the fixed order article → product → topic → scope, capped at `MAX_SETS`. Within a
 * kind WordPress's order holds (the article's own type first, then its scope's other types in
 * the tree's order); a set repeated (same kind and id) keeps its first copy. `excludePollIds` are
 * polls the page already shows elsewhere (the article's conversation poll: in the body, the rail,
 * the panel and the bar — a fifth copy at the end adds nothing but a second composer); they leave
 * every set, and a set left empty is dropped. With nothing left, the section renders nothing.
 * WordPress already answers in this order; the rule is kept here so a reordered or duplicated
 * answer never reorders the chips.
 */
export function orderSets(
  sets: readonly DSPollSet[],
  excludePollIds: readonly string[] = []
): DSPollSet[] {
  const seen = new Set<string>()
  const excluded = new Set(excludePollIds)
  const out: DSPollSet[] = []
  for (const set of [...sets].sort((a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind])) {
    if (seen.has(setKey(set))) {
      continue
    }
    const polls = set.polls.filter(p => !excluded.has(p.id))
    if (polls.length === 0) {
      continue
    }
    seen.add(setKey(set))
    out.push(polls.length === set.polls.length ? set : { ...set, polls })
  }
  return out.slice(0, MAX_SETS)
}

/** A set of one is a card, not a journey: no pager, no position, no end card, no progress line. */
export function isSoloSet(set: Pick<DSPollSet, 'polls'>): boolean {
  return set.polls.length === 1
}

/** A set's chip count: `n` untouched, `done/n` in progress, a tick when every poll is voted. */
export function setProgress(
  set: DSPollSet,
  voted: (pollId: string) => boolean
): { done: number; total: number; finished: boolean } {
  const total = set.polls.length
  const done = set.polls.filter(p => voted(p.id)).length
  return { done, total, finished: total > 0 && done === total }
}

/** Opening a set lands on its first unvoted poll — the first card when every one is voted. */
export function firstOpenIndex(set: DSPollSet, voted: (pollId: string) => boolean): number {
  const index = set.polls.findIndex(p => !voted(p.id))
  return index === -1 ? 0 : index
}

/**
 * The commentHub container of a cross-article poll's own thread: `<prefix>_poll_<id>`, the
 * prefix being the one the site's article containers use (`sg_165223` → `sg`), else the
 * fallback. commentHub registers it on first lookup (kind `poll`).
 */
export function pollContainerId(
  articleContainerId: string | null,
  pollId: string,
  fallbackPrefix = 'sg'
): string {
  const match = articleContainerId ? /^([a-z0-9]+)_\d+$/.exec(articleContainerId) : null
  return `${match ? match[1] : fallbackPrefix}_poll_${pollId}`
}

/**
 * Where a poll's "say why" lands (decision 1): an article-scope poll → the article's thread (null
 * when the page has none — a sponsored post, a versus page); a cross-scope poll → its own thread,
 * which exists even where the article has none.
 */
export function threadFor(poll: DSPoll, articleContainerId: string | null): string | null {
  if (poll.scope === 'cross') {
    return pollContainerId(articleContainerId, poll.id)
  }
  return articleContainerId
}

/** The chip for a set a reader can keep going with: polls left, else nothing. */
export function pollsLeft(set: DSPollSet, voted: (pollId: string) => boolean): number {
  return set.polls.filter(p => !voted(p.id)).length
}

/**
 * The section's sub-line: the sets in words, in chip order — "Polls on this article, then on
 * wireless earbuds, wired earbuds and all earbuds. Results show after you vote."
 */
export function sectionSubtitle(sets: readonly DSPollSet[]): string {
  const parts = sets.map(s => {
    switch (s.kind) {
      case 'article':
        return 'this article'
      case 'product':
        return 'its product'
      default:
        return setChipName(s).toLowerCase()
    }
  })
  if (parts.length === 0) {
    return 'Results show after you vote.'
  }
  const [first, ...rest] = parts
  const last = rest.pop()
  let on = first
  if (last) {
    on = `${first}, then on ${rest.length ? `${rest.join(', ')} and ` : ''}${last}`
  }
  return `Polls on ${on}. Results show after you vote.`
}

/** The end card's title. */
export function endCardTitle(done: number, total: number): string {
  if (total > 0 && done === total) {
    return `You’ve voted on all ${total} ${total === 1 ? 'poll' : 'polls'}`
  }
  return `${done} of ${total} voted`
}

export const ALL_DONE_LINE = 'That’s all for now. New polls go up every week.'
export const KEEP_GOING_LINE = 'Keep going with another set.'
export const SKIPPED_LINE = 'Swipe back for the ones you skipped.'
export const RESULTS_AFTER_VOTE = 'results after you vote'
export const VOTE_TO_SAY_WHY = 'Vote above to say why.'
export const POSTED_WITH_PICK = 'Posted with your pick'
export const EMPTY_THREAD_LINE = 'Vote, then be the first to say why.'

/** The thread is the reward for voting (Guillaume, 2026-10-29): before the vote the section
 *  draws ONE line in the thread's place — no composer, no empty state, no comments (their pick
 *  tags would leak the results). `total` is the thread's comment count once the preview answers;
 *  a cross poll's thread holds reasons alone, an article poll's the whole conversation. */
export function voteFirstLine(scope: DSPoll['scope'] | undefined, total: number | null): string {
  if (!total) {
    return VOTE_TO_SAY_WHY
  }
  if (scope === 'cross') {
    return `${total} ${
      total === 1 ? 'reader' : 'readers'
    } said why. Vote to read their reasons and add yours.`
  }
  return `${VOTE_TO_SAY_WHY} ${total} ${total === 1 ? 'comment' : 'comments'} so far.`
}

/** After the vote the thread draws at most this many top-level rows before "Show all N". */
export const THREAD_ROWS = 3
/** The camp chips (filter by pick) appear from this many top-level threads. */
export const CAMP_CHIPS_FROM = 6

/** The cap's button: a cross poll's rows are reasons, an article poll's are comments. */
export function showAllLabel(scope: DSPoll['scope'] | undefined, total: number): string {
  const noun = scope === 'cross' ? 'reasons' : 'comments'
  return `Show all ${total} ${noun}`
}

/** The sign-in dialog's copy from the section's read-only composer. */
export const SIGN_IN_TO_SAY_WHY = {
  title: 'Sign in to say why',
  subtitle: 'Your pick stays with you.',
}
